import { NavigateFunction } from 'react-router-dom';
import dayjs from 'dayjs';

export const getFormattedUserName = (
  userName: string | null | undefined
) => {
  const userNameArray = userName
    ?.split('/')?.[0]
    .split(' ');
  const length = userNameArray?.length;

  if (!length || !userNameArray[0].length) {
    return undefined;
  } else {
    let name = userNameArray[0];
    let shortName = name[0];
    const lastName = userNameArray[length - 1];
    if (length > 1 && !!lastName.length) {
      name = name + ' ' + lastName;
      shortName = shortName + lastName[0];
    }
    return { name, shortName };
  }
};

export const updateQueryParams = (
  newParams: object,
  navigate: NavigateFunction
) => {
  const queryParams = new URLSearchParams(
    window.location.search
  );
  for (const [key, value] of Object.entries(newParams)) {
    if (value === undefined || value === null) {
      queryParams.delete(key);
    } else {
      queryParams.set(key, value);
    }
  }
  navigate(`?${queryParams.toString()}`);
};

export const tokenExpiringInMs = (
  token: string | undefined,
  timeInMs: number
) => {
  if (!token) return true;
  const expiry = JSON.parse(atob(token.split('.')[1])).exp;
  return dayjs.unix(expiry).diff(dayjs()) < timeInMs;
};

/**
 * Generic function to download a file from a URL or Blob.
 * Works with blob URLs, regular URLs, and cross-origin images.
 * 
 * @param url - The URL or blob URL to download
 * @param filename - Optional filename. If not provided, will be extracted from URL
 * @param fetchBlob - Optional function to fetch the blob if URL needs to be fetched first
 */
export const downloadFile = async (
  url: string,
  filename?: string,
  fetchBlob?: () => Promise<Blob>
): Promise<void> => {
  try {
    let blob: Blob;
    let finalFilename = filename;

    // If it's a blob URL, fetch it directly
    if (url.startsWith('blob:')) {
      const response = await fetch(url);
      blob = await response.blob();
      if (!finalFilename) {
        finalFilename = 'download';
      }
    } else if (fetchBlob) {
      // Use provided fetch function (e.g., for API calls)
      blob = await fetchBlob();
      if (!finalFilename) {
        // Try to extract filename from URL
        try {
          const urlObj = new URL(url);
          const pathParts = urlObj.pathname.split('/').filter(Boolean);
          finalFilename = pathParts[pathParts.length - 1] || 'download';
        } catch {
          finalFilename = 'download';
        }
      }
    } else {
      // Fetch from regular URL
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }
      blob = await response.blob();
      if (!finalFilename) {
        // Try to extract filename from URL or Content-Disposition header
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch) {
            finalFilename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        if (!finalFilename) {
          try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/').filter(Boolean);
            finalFilename = pathParts[pathParts.length - 1] || 'download';
          } catch {
            finalFilename = 'download';
          }
        }
      }
    }

    // Ensure filename has an extension
    if (!finalFilename.includes('.')) {
      // Try to determine extension from blob type
      const extension = blob.type.split('/')[1]?.split(';')[0] || 'png';
      finalFilename = `${finalFilename}.${extension}`;
    }

    // Create download link and trigger download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};
